import {
  MarketButton, MarketAccessory, MarketButtonDropdown, MarketList, MarketRow, MarketModalPartial,
  MarketHeader,
  MarketBanner,
  MarketTooltip,
  MarketLink,
  MarketDropdown,
  MarketPopover,
} from '@market/react';
import classNames from 'classnames';
import type { TFunction } from 'i18next';
import {
  type JSX,
  useRef, useState, type ReactNode, type ComponentType,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MarketSkeletonLoader } from '@squareup/market-react';
import {
  MarketArrowLeftIcon,
  MarketArrowRightIcon,
  MarketCardSwipeIcon,
  MarketChartBarIcon,
  MarketChevronRightIcon,
  MarketCopyIcon,
  MarketDogearedPaperIcon,
  MarketGearIcon,
  MarketLoudSpeakerIcon,
  MarketMessageIcon,
  MarketPlusIcon,
  MarketQuestionMarkCircleIcon,
  MarketReaderTapIcon,
  MarketRightLeftIcon,
  MarketUpRightIcon,
  type MarketIconProps,
} from '@squareup/market-react/icons';
import { MarketTooltip as NewMarketTooltip } from '@squareup/market-react/trial';
import { formatCurrency } from '../../../utils';
import { buildMarketBanner } from '../../../utils/build-market-banner';
import { buildMarketList } from '../../../utils/build-market-list';
import type { MoneyUnion } from '../../../utils/format-currency';
import { LocationSelector } from '../location-selector/LocationSelector';
import type {
  TBalanceSubText,
  BannerProps,
  HeaderButton,
  SettingsMenuRow,
  Subunit,
  HeaderDropdown,
  AccountProps,
  BalanceSubText,
} from './ProductPageHeader';
import styles from './ProductPageHeader.module.scss';

type MarketIconComponent = ComponentType<MarketIconProps>;

const ICON_MAP: Record<string, MarketIconComponent> = {
  'arrow-right': MarketArrowRightIcon,
  'card-swipe': MarketCardSwipeIcon,
  'chart-bar': MarketChartBarIcon,
  'dogeared-paper': MarketDogearedPaperIcon,
  gear: MarketGearIcon,
  'loud-speaker': MarketLoudSpeakerIcon,
  message: MarketMessageIcon,
  plus: MarketPlusIcon,
  'question-mark-circle': MarketQuestionMarkCircleIcon,
  'reader-tap': MarketReaderTapIcon,
  'right-left': MarketRightLeftIcon,
  'up-right': MarketUpRightIcon,
};

export const GoBack = ({ t, goBack }: { t: TFunction; goBack: () => void }) => (
  <MarketButton
    icon-only
    className={styles.goBack}
    rank="secondary"
    size="medium"
    aria-label={t('bankingShared.goBack')}
    onClick={goBack}
  >
    <MarketAccessory slot="icon">
      <MarketArrowLeftIcon />
    </MarketAccessory>
  </MarketButton>
);

export const Settings = ({
  t,
  goToSettings,
  settingsMenuRows,
  iconType = 'settings',
  tooltipText,
}: {
  t: TFunction;
  goToSettings?: () => void;
  settingsMenuRows?: SettingsMenuRow[];
  iconType?: 'settings' | 'help';
  tooltipText?: ReactNode;
}) => {
  const isHelp = iconType === 'help';
  const ariaLabel = isHelp ? t('bankingShared.help') : t('bankingShared.settings');
  const IconComponent = isHelp ? MarketQuestionMarkCircleIcon : MarketGearIcon;

  if (tooltipText) {
    return (
      <div className={styles.settings}>
        <NewMarketTooltip>
          <NewMarketTooltip.Trigger>
            <MarketButton
              icon-only
              rank="secondary"
              size="medium"
              aria-label={ariaLabel}
            >
              <MarketAccessory slot="icon">
                <IconComponent />
              </MarketAccessory>
            </MarketButton>
          </NewMarketTooltip.Trigger>
          <NewMarketTooltip.Content className={styles.settingsTooltipContent}>{tooltipText}</NewMarketTooltip.Content>
        </NewMarketTooltip>
      </div>
    );
  }

  if (goToSettings) {
    return (
      <div className={styles.settings}>
        <MarketButton
          icon-only
          rank="secondary"
          size="medium"
          aria-label={ariaLabel}
          onClick={goToSettings}
        >
          <MarketAccessory slot="icon">
            <IconComponent />
          </MarketAccessory>
        </MarketButton>
      </div>
    );
  }

  if (settingsMenuRows?.length) {
    return (
      <div className={styles.settings}>
        <MarketDropdown popoverPlacement="bottom-end">
          <MarketButton
            icon-only
            rank="secondary"
            size="medium"
            aria-label={ariaLabel}
            slot="trigger"
          >
            <MarketAccessory slot="icon">
              <IconComponent />
            </MarketAccessory>
          </MarketButton>
          <MarketPopover slot="popover">
            <MarketList interactive>
              {settingsMenuRows.map((row) => {
                const RowIconComponent = row.icon ? ICON_MAP[row.icon] : undefined;
                return (
                  <MarketRow
                    key={row.title}
                    transient={!!row.onClick}
                    className={classNames(row.className, !row.onClick && styles.nonInteractiveRow)}
                    onClick={row.onClick}
                  >
                    {RowIconComponent && (
                      <MarketAccessory size="icon" slot="leading-accessory">
                        <RowIconComponent />
                      </MarketAccessory>
                    )}
                    <label slot="label">{row.title}</label>
                  </MarketRow>
                );
              })}
            </MarketList>
          </MarketPopover>
        </MarketDropdown>
      </div>
    );
  }
};

export const AccountButton = ({ t, account, isMobile }: { t: TFunction; account: AccountProps; isMobile: boolean }) => {
  const [showAccountModal, setShowAccountModal] = useState(false);
  const { accountSuffix, accountNumber, routingNumber } = account || {};

  if (accountSuffix || accountNumber) {
    const last3 = String((accountSuffix || accountNumber || '')).slice(-3);
    return (
      <div className={styles.accountNumber}>
        {isMobile
          ? (
            <MarketLink onClick={() => setShowAccountModal(true)}>
              {t('bankingShared.maskAccountNumber', { suffix: last3 })}
              <MarketChevronRightIcon size="small" />
            </MarketLink>
          )
          : (
            <MarketButton
              rank="secondary"
              onClick={() => setShowAccountModal(true)}
            >
              {t('bankingShared.maskAccountNumber', { suffix: last3 })}
            </MarketButton>
          )}

        {showAccountModal && <AccountModal
          t={t}
          accountNumber={accountNumber}
          routingNumber={routingNumber}
          onClose={() => setShowAccountModal(false)} />}
      </div>
    );
  }
  return null;
};

const AccountModal = ({
  t, accountNumber, routingNumber, onClose,
}: { t: TFunction; accountNumber?: string; routingNumber?: string; onClose: () => void }) => {
  const [isAccountNumberCopied, setIsAccountNumberCopied] = useState(false);
  const [isRoutingNumberCopied, setIsRoutingNumberCopied] = useState(false);
  const handleCopy = async (text: string, setCopied: (bool: boolean) => void) => {
    await window.navigator?.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  // formatting the account and routing numbers in 3 + 3 + 3 + rest format
  const formatNumber = (number: string) => (String(number).replace(/^(\d{3})(\d{3})(\d{3})(\d*)$/, (_, g1, g2, g3, rest) => [g1, g2, g3, rest].filter(Boolean).join(' ')));

  return (
    <MarketModalPartial trapFocus onMarketDialogDismissed={onClose}>
      <MarketHeader><h2>{t('bankingShared.accountModal.title')}</h2></MarketHeader>
      <div>
        <MarketRow>
          <label slot="label">{t('bankingShared.accountModal.accountNumber')}</label>
          {accountNumber ?
            <>
              <p slot="subtext">{formatNumber(accountNumber)}</p>
              <MarketButton
                slot="trailing-accessory"
                rank="secondary"
                onClick={() => handleCopy(accountNumber ?? '', setIsAccountNumberCopied)}
              >
                <MarketAccessory slot="icon"><MarketCopyIcon /></MarketAccessory>
                {isAccountNumberCopied ? t('bankingShared.accountModal.copied') : t('bankingShared.accountModal.copy')}
              </MarketButton>
            </>
            : <MarketSkeletonLoader size="medium" />}
        </MarketRow>
        <MarketRow>
          <label slot="label">{t('bankingShared.accountModal.routingNumber')}</label>
          {routingNumber ?
            <>
              <p slot="subtext">{formatNumber(routingNumber)}</p>
              <MarketButton
                slot="trailing-accessory"
                rank="secondary"
                onClick={() => handleCopy(routingNumber ?? '', setIsRoutingNumberCopied)}
              >
                <MarketAccessory slot="icon"><MarketCopyIcon /></MarketAccessory>
                {isRoutingNumberCopied ? t('bankingShared.accountModal.copied') : t('bankingShared.accountModal.copy')}
              </MarketButton>
            </>
            : <MarketSkeletonLoader size="medium" />}
        </MarketRow>
        <MarketBanner>
          <span slot="title">{t('bankingShared.accountModal.guideTitle')}</span>
          {t('bankingShared.accountModal.guideDescription')}
          <a
            slot="action"
            target="_blank"
            rel="noopener noreferrer"
            href="https://squareup.com/help/us/en/article/7604-square-checking-transfers"
          >
            {t('bankingShared.accountModal.learnMore')}
          </a>
        </MarketBanner>
      </div>
    </MarketModalPartial>
  );
};

export const ProductPageLocationSelector = ({
  currentUnitToken,
  units,
  handleSelectLocation,
  locationSelector,
  shouldCenterTitle,
}: {
  currentUnitToken?: string;
  units?: Subunit[];
  handleSelectLocation?: (location: Subunit) => void;
  locationSelector?: JSX.Element;
  shouldCenterTitle?: boolean;
}) => {
  if (locationSelector) {
    return (
      <span
        className={styles.locationSelector}
        data-testid="product-page-header-location-selector"
      >
        {locationSelector}
      </span>);
  }

  if (units && units.length > 1) {
    return (
      <span
        className={styles.locationSelector}
        data-testid="product-page-header-location-selector"
      >
        <DefaultLocationSelector
          handleSelectLocation={handleSelectLocation}
          currentUnitToken={currentUnitToken}
          locations={units}
          shouldCenterTitle={shouldCenterTitle}
        />
      </span>);
  }

  return null;
};

const DefaultLocationSelector = ({
  handleSelectLocation,
  currentUnitToken,
  locations,
  shouldCenterTitle,
}: {
  handleSelectLocation?: (location: Subunit) => void;
  currentUnitToken?: string;
  locations: Subunit[];
  shouldCenterTitle?: boolean;
}) => {
  const selectedLocation = locations.find((location) => location.token === currentUnitToken) ?? locations[0];
  // truncate selected location name when character is 24 and above, inherited from old behavior
  const displayedLocationLabel = selectedLocation?.nickname && selectedLocation.nickname.length > 23
    ? `${selectedLocation.nickname.slice(0, 23)}...`
    : selectedLocation?.nickname ?? '';
  const dropdownRef = useRef<HTMLMarketButtonDropdownElement>(null);
  const [searchInput, setSearchInput] = useState('');

  const searchLocation = () => {
    const normalizedQuery = searchInput.trim().toLowerCase();

    const filtered = normalizedQuery
      ? locations
        .filter((location) => location.nickname.toLowerCase().includes(normalizedQuery))
        .sort((a, b) => a.nickname.localeCompare(b.nickname))
      : locations;

    const result = filtered.map((location) => ({
      locationId: location.token,
      locationLabel: location.nickname,
      rowState: location.rowState,
      selectLocation: () => handleSelectLocation?.(location),
    }));

    return result;
  };

  return (
    <LocationSelector
      selectedLocationId={selectedLocation.token}
      selectedLocationLabel={displayedLocationLabel}
      locations={searchLocation()}
      searchInputValue={searchInput}
      setSearchInputValue={setSearchInput}
      dropdownRef={dropdownRef}
      dropdownProps={{
        popoverPlacement: shouldCenterTitle ? 'bottom' : 'bottom-start',
      }}
    />
  );
};

function isBalanceSubTextArray(v: TBalanceSubText): v is BalanceSubText[] {
  return Array.isArray(v) && v.every(
    (i) => i && typeof i === 'object' && 'text' in i,
  );
}

export const Balance = ({
  balance,
  balanceSuperText,
  balanceSubText,
  shouldAddBottomSpacer,
}: {
  balance: MoneyUnion;
  balanceSuperText?: string;
  balanceSubText?: TBalanceSubText;
  shouldAddBottomSpacer?: boolean;
}) => {
  const { i18n } = useTranslation();

  return (
    <>
      {(balanceSuperText || balance) && <div aria-hidden className={styles.spacerTop} />}
      {balanceSuperText && <p className={`paragraph-30 ${styles.balanceSuperText}`}>{balanceSuperText}</p>}
      <div className={styles.balance} data-testid="product-page-header-balance">
        {formatCurrency({ currencyValue: balance, locale: i18n.language })}
      </div>
      {balanceSubText && (
        <div className={styles.balanceSubText}>
          {isBalanceSubTextArray(balanceSubText)
            ? balanceSubText.map((subText, index) => (
              // note: use the index as the key because there is no better solution when we receive a ReactNode as the `.text` property.
              <div key={index} className={`${subText.fontWeight === 'bold' ? 'medium-30' : 'paragraph-30'} ${styles.balanceSubTextRow}`}>
                {subText.iconName && (
                  <span className={styles.balanceSubTextIcon}>
                    {(() => {
                      const SubTextIcon = subText.iconName ? ICON_MAP[subText.iconName] : undefined;
                      return SubTextIcon
                        ? <SubTextIcon aria-label={subText.iconName} style={{ width: 20, height: 20 }} />
                        : null;
                    })()}
                  </span>
                )}
                {subText.text}
                {subText.tooltipContent && (
                  <MarketTooltip data-testid="product-page-header-inline-tooltip">
                    <span slot="content">
                      <Trans defaults={String(subText.tooltipContent)} />
                    </span>
                  </MarketTooltip>
                )}
              </div>
            ))
            : balanceSubText
          }
        </div>
      )}
      {shouldAddBottomSpacer && <div aria-hidden className={styles.spacerBottom} />}
    </>
  );
};

export const Buttons = ({
  buttons,
  isMobile,
  shouldWrap,
}: {
  buttons: HeaderButton[];
  isMobile: boolean;
  shouldWrap: boolean;
}) => (
  <div className={`${styles.buttons} ${shouldWrap ? styles.buttonsWrapped : ''}`}>
    {buttons.map((button) => {
      const ButtonIconComponent = button.icon ? ICON_MAP[button.icon] : undefined;
      return (
        <MarketButton
          key={button.copy}
          rank="primary"
          className={styles.buttonPrimary}
          disabled={button.isDisabled}
          is-loading={button.isLoading}
          data-testid="product-page-header-button"
          onClick={button.onClick}
        >
          {ButtonIconComponent && !isMobile && (
            <MarketAccessory slot="icon">
              <ButtonIconComponent
                style={{ color: button.isDisabled ? 'var(--button-normal-variant-primary-rank-disabled-state-label-color)' : 'var(--core-fill-white-color)' }}
              />
            </MarketAccessory>
          )}
          {button.copy}
        </MarketButton>
      );
    })
    }
  </div>
);

export const ButtonDropdown = ({ buttonDropdown }: { buttonDropdown: HeaderDropdown }) => (
  <div className={styles.buttons}>
    <MarketButtonDropdown
      data-testid="product-page-header-button-dropdown"
      disabled={buttonDropdown.isDisabled}
      onClick={buttonDropdown.onClick}
    >
      <MarketButton rank="primary" slot="trigger">{buttonDropdown.copy}</MarketButton>
      {buildMarketList(buttonDropdown.contentList)}
    </MarketButtonDropdown>
  </div>
);

export const Banners = ({ banners }: { banners: BannerProps[] }) => (
  <div className={styles.banners}>
    {banners.map((banner) => (
      buildMarketBanner(banner)
    ))}
  </div>
);

export const Error = ({ t, onTryAgain }: { t: TFunction; onTryAgain?: () => void }) => (
  <div className={styles.error} data-testid="product-page-header-error">
    <div className={`heading-20 ${styles.errorText}`}>{t('bankingShared.error.message')}</div>
    {onTryAgain && (
      <MarketButton data-testid="product-page-header-try-again" onClick={onTryAgain}>
        {t('bankingShared.error.tryAgain')}
      </MarketButton>
    )}
  </div>
);

export const Loading = () => (
  <div className={styles.loading} data-testid="product-page-header-loading">
    <MarketSkeletonLoader size="medium" />
    <MarketSkeletonLoader size="small" />
  </div>
);
